import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import CryptoJS from 'crypto-js';

const ClientAuthContext = createContext();

// Secret key for encryption
const SECRET_KEY = 'your-strong-secret-key';

// Encrypt data
const encryptData = (data) => {
  if (!data) return null;
  return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
};

// Decrypt data
const decryptData = (encryptedData) => {
  if (!encryptedData) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    return decryptedString ? JSON.parse(decryptedString) : null;
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};


export const ClientAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [accessToken, setAccessToken] = useState(decryptData(localStorage.getItem('accessToken')));
  const [refreshToken, setRefreshToken] = useState(decryptData(localStorage.getItem('refreshToken')));
  const [ClientId, setClientId] = useState(decryptData(localStorage.getItem('ClientId')));
  const [username, setUsername] = useState(decryptData(localStorage.getItem('username')));
  const [loading, setLoading] = useState(true);

  // Helper function to refresh the access token
  const refreshAccessToken = async () => {
    try {
      const response = await axios.post(`${config.apiBASEURL}/ClientAuthRoutes/refresh-token`, {
        refreshToken,
      });

      const { accessToken: newAccessToken } = response.data;
      setAccessToken(newAccessToken);
      localStorage.setItem('accessToken', encryptData(newAccessToken));
      return newAccessToken;
    } catch (error) {
      console.error('Error refreshing access token:', error);
      logout();
    }
  };

  // Helper function to handle API requests with token refresh
  const apiRequest = async (url, options = {}) => {
    const { method = 'GET', data = null, params = {}, headers = {} } = options;

    try {
      const response = await axios({
        method,
        url,
        data,
        params, // <== Add this line!
        headers: { ...headers, Authorization: `Bearer ${accessToken}` },
      });
      return response;
    } catch (error) {
      if (error.response?.status === 403) {
        console.warn('403 Forbidden: Attempting to refresh token.');
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          try {
            const retryResponse = await axios({
              method,
              url,
              data,
              params, // <== Add this here too!
              headers: { ...headers, Authorization: `Bearer ${newAccessToken}` },
            });
            return retryResponse;
          } catch (retryError) {
            console.error('Error retrying request after token refresh:', retryError);
            throw retryError;
          }
        }
      }
      console.error('API request failed:', error);
      throw error;
    }
  };
 
  const checkAuthStatus = async () => {
    if (accessToken) {
      try {
        const response = await apiRequest(`${config.apiBASEURL}/ClientAuthRoutes/Client-status`);
        const { isAuthenticated, username, role, ClientId } = response.data;

        setIsAuthenticated(isAuthenticated);
        setRole(role);
        setClientId(ClientId);
        setUsername(username);

        // Store user details in session storage (encrypted)
        localStorage.setItem('role', encryptData(role));
        localStorage.setItem('ClientId', encryptData(ClientId));
        localStorage.setItem('username', encryptData(username));
      } catch (error) {
        console.error('Error checking authentication status:', error);
        logout(); // Clear session if the check fails
      }
    } else {
      logout();
    }
    setLoading(false);
  };


  useEffect(() => {
    if (accessToken) {
      checkAuthStatus();
    } else {
      setLoading(false); // Prevent infinite loading state
    }
  
    const intervalId = setInterval(() => {
      if (accessToken) checkAuthStatus();
    }, 1800000); // 30 minutes
  
    return () => clearInterval(intervalId);
  }, [accessToken]);

  const login = (authData) => {
    // Backend Client-login returns: ClientId, name (not Client_id / username)
    const resolvedClientId = authData.ClientId ?? authData.Client_id;
    const resolvedUsername = authData.name ?? authData.username;

    setIsAuthenticated(true);
    setRole(authData.role);
    setAccessToken(authData.accessToken);
    setRefreshToken(authData.refreshToken);
    setClientId(resolvedClientId);
    setUsername(resolvedUsername);

    // Store the tokens and user details in session storage (encrypted)
    localStorage.setItem('accessToken', encryptData(authData.accessToken));
    localStorage.setItem('refreshToken', encryptData(authData.refreshToken)); // Persist refresh token
    localStorage.setItem('role', encryptData(authData.role));
    localStorage.setItem('ClientId', encryptData(resolvedClientId));
    localStorage.setItem('username', encryptData(resolvedUsername));

  };

  const logout = async () => {
    if (!isAuthenticated) return; // Prevent duplicate logout calls
  
    try {
      await apiRequest(`${config.apiBASEURL}/ClientAuthRoutes/Client-logout`, {
        method: 'POST',
        data: { refreshToken },
      });
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setIsAuthenticated(false);
      setRole(null);
      setClientId(null);
      setUsername(null);
      setAccessToken(null);
      setRefreshToken(null);
      localStorage.clear();
    }
  };
  


  return (
    <ClientAuthContext.Provider
      value={{
        isAuthenticated,
        role,
        ClientId,
        username,
        accessToken,
        apiRequest,
        login,
        logout,
        loading,
      }}
    >
      {!loading && children}
    </ClientAuthContext.Provider>
  );
};

export const useClientAuth = () => useContext(ClientAuthContext);
